import { createDomain, sample } from 'effector'
import { AxiosErrorType, DriverRegistrationRequest } from '~/shared/api'
import { replaceFx } from '~/shared/lib/history'
import { loadingModel } from '~/shared/lib/loading'
import { logger } from '~/shared/lib/logger'
import {
  mapJsonApiMessageErrors,
  mapMessageErrors,
} from '~/shared/lib/mapMessageErrors'
import { snackbarEnqueued } from '~/shared/lib/notifications'
import { isString } from '~/shared/lib/utils'
import { formProviderModel } from '~/shared/ui/form'
import { loginAfterFullRegistration } from '../auth/Login/model'
import { additionalDataFormModel } from './AdditionalData'
import { correctionFormModel } from './CorrectionStep'
import { driverLicenseFormModel } from './DriverLicenseStep'
import { omitValidationFields } from './helpers'
import { passportFormModel } from './PassportStep'
import { goToErrorStep, goToSuccessStep } from './stepModel'

const domain = createDomain('features.documentBaseRegistration')

type RegistrationValues = {
  taxiparkId: string
  confirmCode: string
} & Partial<additionalDataFormModel.FormValues> &
  Partial<passportFormModel.FormValues> &
  Partial<driverLicenseFormModel.FormValues> &
  Partial<correctionFormModel.FormValues>

export const registration = domain.createEvent<RegistrationValues>()
export const registrationFx = domain.createEffect<
  RegistrationValues,
  { token: string; phone: string },
  AxiosErrorType
>({
  async handler({
    regionCode,
    aggregatorOccupationOption,
    taxSystem,
    taxiparkId,
    passportPhotos,
    drivingLicensePhotos,
    ...arg
  }) {
    const driver = new DriverRegistrationRequest({
      ...omitValidationFields(arg),
      ...(isString(regionCode) ? { regionCode } : {}),
      ...(isString(taxSystem) ? { taxSystem } : {}),
      passportPhotos: passportPhotos?.filter(isString),
      drivingLicensePhotos: drivingLicensePhotos?.filter(isString),
    })

    driver.setTaxipark(taxiparkId)
    if (isString(aggregatorOccupationOption))
      driver.setAggregatorOccupation(aggregatorOccupationOption)

    const response = await driver.save()
    const data = response.getHttpClientResponse().getData()
    const token = data?.meta?.token
    const phone = data?.data?.attributes?.phone
    return { token, phone }
  },
})

sample({
  clock: registration,
  target: registrationFx,
})

sample({
  clock: registrationFx.doneData,
  filter: ({ token }) => Boolean(token),
  target: loginAfterFullRegistration,
})

sample({
  clock: loginAfterFullRegistration,
  fn: () => '/booking-cars',
  target: replaceFx,
})

sample({
  clock: registrationFx.doneData,
  filter: ({ token }) => !token,
  target: goToSuccessStep,
})

sample({
  clock: registrationFx.failData,
  fn(e) {
    logger.error(e)
    return {
      message: mapJsonApiMessageErrors(e),
      variant: 'danger' as const,
    }
  },
  target: snackbarEnqueued,
})

sample({
  clock: registrationFx.failData,
  filter: (e) => e?.response?.status !== 422,
  target: goToErrorStep,
})

export const sendCode = domain.createEvent<{ phone: string; fn: () => void }>()
export const sendCodeFx = domain.createEffect<
  { phone: string; fn: () => void },
  string,
  AxiosErrorType
>({
  handler: async ({ phone, fn }) => {
    await DriverRegistrationRequest.sendCode(phone)
    fn()
    return phone
  },
})

sample({
  clock: sendCode,
  target: sendCodeFx,
})

sample({
  clock: sendCodeFx.doneData,
  fn(phone) {
    return {
      message: `Код подтверждения отправлен на телефон ${phone}`,
      variant: 'success' as const,
    }
  },
  target: snackbarEnqueued,
})

sample({
  clock: sendCodeFx.failData,
  fn(e) {
    return {
      message: mapMessageErrors(e),
      variant: 'danger' as const,
    }
  },
  target: snackbarEnqueued,
})

export const validate = domain.createEvent<{
  formValues: Partial<RegistrationValues>
  taxiparkId?: UniqueId
  successFn: () => void
  formSetError: formProviderModel.FormSetError
}>()

export const validateFx = domain.createEffect<
  {
    formValues: Partial<RegistrationValues>
    taxiparkId?: string
    successFn: () => void
  },
  void,
  AxiosErrorType
>({
  async handler({
    formValues: {
      regionCode,
      aggregatorOccupationOption,
      taxSystem,
      passportPhotos,
      drivingLicensePhotos,
      ...attr
    },
    taxiparkId,
    successFn,
  }) {
    const driver = new DriverRegistrationRequest({
      ...omitValidationFields({ ...attr }),
      ...(isString(regionCode) ? { regionCode } : {}),
      ...(isString(taxSystem) ? { taxSystem } : {}),
      passportPhotos: passportPhotos?.filter(isString),
      drivingLicensePhotos: drivingLicensePhotos?.filter(isString),
    })

    if (taxiparkId) driver.setTaxipark(taxiparkId)
    if (isString(aggregatorOccupationOption))
      driver.setAggregatorOccupation(aggregatorOccupationOption)

    await driver.validate()
    successFn()
  },
})

sample({
  clock: validate,
  target: validateFx,
})

sample({
  clock: validate,
  fn: () => ({
    message: 'Проверка данных',
  }),
  target: loadingModel.present,
})

sample({
  clock: validateFx.failData,
  source: validate,
  filter: ({ formSetError }, e) =>
    !!formSetError && e?.response?.status === 422,
  fn: ({ formSetError }, error) => ({ error, formSetError }),
  target: formProviderModel.triggerErrorFx,
})

sample({
  clock: validateFx.failData,
  fn(e) {
    logger.error(e)
    return {
      message: mapJsonApiMessageErrors(e),
      variant: 'danger' as const,
    }
  },
  target: snackbarEnqueued,
})

sample({
  clock: validateFx.finally,
  target: loadingModel.dismiss,
})
